// /src/hooks/useQualityReports.ts
import { useState, useCallback } from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface QualityReportData {
  complianceData: Array<{
    month: string;
    zgodne: number;
    warunkowe: number;
    niezgodne: number;
  }>;
  defectTypes: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  productQuality: Array<{
    product: string;
    checks: number;
    passed: number;
    passRate: number;
  }>;
  statistics: {
    totalChecks: number;
    avgComplianceRate: number;
    avgResponseTime: number;
    nonComplianceCost: number;
  };
}

export const useQualityReports = () => {
  const { supabase } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Pobierz dane do raportów
  const fetchReportData = useCallback(async (dateRange: { from: string; to: string }) => {
    setLoading(true);
    setError(null);
    
    try {
      // Zamiast pobierać z bazy, wygeneruj przykładowe dane
      const controls = generateMockQualityData(dateRange);
      
      // Przetwórz dane na wykresy
      const reportData = processReportData(controls, dateRange);
      
      return { data: reportData, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Błąd pobierania danych raportu');
      setError(error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, []);

  // Generuj raport PDF
  const generateReport = useCallback(async (type: string, dateRange: { from: string; to: string }) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: reportData } = await fetchReportData(dateRange);
      
      if (!reportData) {
        throw new Error('Brak danych do raportu');
      }

      const fromDate = new Date(dateRange.from).toLocaleDateString('pl-PL');
      const toDate = new Date(dateRange.to).toLocaleDateString('pl-PL');
      
      switch(type) {
        case 'monthly': {
          // Raport miesięczny jakości - PDF
          const pdf = new jsPDF();
          
          // Nagłówek
          pdf.setFontSize(20);
          pdf.text('Raport Miesięczny Kontroli Jakości', 105, 20, { align: 'center' });
          pdf.setFontSize(12);
          pdf.text(`Okres: ${fromDate} - ${toDate}`, 105, 30, { align: 'center' });
          
          // Statystyki ogólne
          pdf.setFontSize(14);
          pdf.text('Podsumowanie:', 20, 45);
          pdf.setFontSize(11);
          pdf.text(`Całkowita liczba kontroli: ${reportData.statistics.totalChecks}`, 30, 55);
          pdf.text(`Średni wskaźnik zgodności: ${reportData.statistics.avgComplianceRate}%`, 30, 62);
          pdf.text(`Średni czas reakcji: ${reportData.statistics.avgResponseTime}h`, 30, 69);
          pdf.text(`Koszty niezgodności: ${reportData.statistics.nonComplianceCost.toLocaleString('pl-PL')} zł`, 30, 76);
          
          // Tabela jakości według produktów
          const productTableData = reportData.productQuality.map(p => [
            p.product,
            p.checks.toString(),
            p.passed.toString(),
            `${p.passRate}%`
          ]);
          
          (pdf as any).autoTable({
            head: [['Produkt', 'Kontrole', 'Zgodne', 'Wskaźnik']],
            body: productTableData,
            startY: 85,
            theme: 'grid',
            headStyles: { fillColor: [251, 146, 60] }
          });
          
          // Tabela typów defektów
          const defectTableData = reportData.defectTypes.map(d => [
            d.name,
            d.value.toString(),
            `${Math.round((d.value / reportData.statistics.totalChecks) * 100)}%`
          ]);
          
          const finalY = (pdf as any).lastAutoTable.finalY || 150;
          
          pdf.text('Typy niezgodności:', 20, finalY + 15);
          
          (pdf as any).autoTable({
            head: [['Typ defektu', 'Liczba', 'Udział']],
            body: defectTableData,
            startY: finalY + 20,
            theme: 'grid',
            headStyles: { fillColor: [251, 146, 60] }
          });
          
          // Stopka
          pdf.setFontSize(10);
          pdf.text(`Wygenerowano: ${new Date().toLocaleString('pl-PL')}`, 20, 280);
          
          pdf.save(`Raport_miesiczny_${dateRange.from}_${dateRange.to}.pdf`);
          break;
        }
        
        case 'defects': {
          // Rejestr niezgodności - Excel
          const wb = XLSX.utils.book_new();
          
          // Arkusz z defektami
          const defectData = [];
          
          // Nagłówki
          defectData.push(['Rejestr Niezgodności']);
          defectData.push([`Okres: ${fromDate} - ${toDate}`]);
          defectData.push([]);
          defectData.push(['Typ defektu', 'Liczba wystąpień', 'Udział %', 'Trend']);
          
          // Dane
          reportData.defectTypes.forEach(defect => {
            defectData.push([
              defect.name,
              defect.value,
              `${Math.round((defect.value / reportData.statistics.totalChecks) * 100)}%`,
              'Stabilny'
            ]);
          });
          
          // Podsumowanie
          defectData.push([]);
          defectData.push(['PODSUMOWANIE']);
          defectData.push(['Całkowita liczba niezgodności', reportData.defectTypes.reduce((sum, d) => sum + d.value, 0)]);
          defectData.push(['Koszt niezgodności', `${reportData.statistics.nonComplianceCost.toLocaleString('pl-PL')} zł`]);
          
          const ws = XLSX.utils.aoa_to_sheet(defectData);
          XLSX.utils.book_append_sheet(wb, ws, 'Niezgodności');
          
          // Arkusz ze statystykami
          const statsData = [];
          statsData.push(['Statystyki kontroli jakości']);
          statsData.push([]);
          statsData.push(['Miesiąc', 'Zgodne %', 'Warunkowe %', 'Niezgodne %']);
          
          reportData.complianceData.forEach(month => {
            statsData.push([
              month.month,
              month.zgodne,
              month.warunkowe,
              month.niezgodne
            ]);
          });
          
          const ws2 = XLSX.utils.aoa_to_sheet(statsData);
          XLSX.utils.book_append_sheet(wb, ws2, 'Statystyki');
          
          // Zapisz plik
          XLSX.writeFile(wb, `Rejestr_niezgodnosci_${dateRange.from}_${dateRange.to}.xlsx`);
          break;
        }
        
        case 'trends': {
          // Analiza trendów - PDF z wykresami
          const pdf = new jsPDF();
          
          pdf.setFontSize(20);
          pdf.text('Analiza Trendów Jakości', 105, 20, { align: 'center' });
          pdf.setFontSize(12);
          pdf.text(`Okres: ${fromDate} - ${toDate}`, 105, 30, { align: 'center' });
          
          // Analiza trendów
          pdf.setFontSize(14);
          pdf.text('Kluczowe trendy:', 20, 45);
          pdf.setFontSize(11);
          
          // Oblicz trendy
          const avgCompliance = reportData.statistics.avgComplianceRate;
          const trend = avgCompliance > 95 ? 'pozytywny' : avgCompliance > 90 ? 'stabilny' : 'negatywny';
          const trendColor = trend === 'pozytywny' ? [0, 128, 0] : trend === 'stabilny' ? [255, 165, 0] : [255, 0, 0];
          
          pdf.text(`1. Trend zgodności: ${trend} (${avgCompliance}%)`, 30, 55);
          pdf.text(`2. Najczęstszy typ defektu: ${reportData.defectTypes[0]?.name || 'Brak'}`, 30, 62);
          pdf.text(`3. Produkt o najwyższej jakości: ${reportData.productQuality[0]?.product || 'Brak danych'}`, 30, 69);
          
          // Rekomendacje
          pdf.text('Rekomendacje:', 20, 85);
          
          const recommendations = [];
          if (avgCompliance < 95) {
            recommendations.push('Zwiększyć częstotliwość kontroli w procesie produkcji');
          }
          if (reportData.statistics.avgResponseTime > 5) {
            recommendations.push('Skrócić czas reakcji na wykryte niezgodności');
          }
          if (reportData.defectTypes[0]?.value > 10) {
            recommendations.push(`Skupić się na eliminacji defektów typu: ${reportData.defectTypes[0].name}`);
          }
          
          recommendations.forEach((rec, index) => {
            pdf.text(`${index + 1}. ${rec}`, 30, 95 + (index * 7));
          });
          
          // Prognoza
          pdf.text('Prognoza na następny miesiąc:', 20, 130);
          
          const nextMonthCompliance = trend === 'pozytywny' ? avgCompliance + 2 : 
                                      trend === 'stabilny' ? avgCompliance : 
                                      avgCompliance - 2;
          
          pdf.text(`Przewidywany wskaźnik zgodności: ${Math.min(100, Math.max(0, nextMonthCompliance)).toFixed(1)}%`, 30, 140);
          pdf.text(`Szacowane koszty niezgodności: ${Math.round(reportData.statistics.nonComplianceCost * 0.9).toLocaleString('pl-PL')} zł`, 30, 147);
          
          // Tabela miesięcznego trendu
          const trendTableData = reportData.complianceData.map(m => [
            m.month,
            `${m.zgodne}%`,
            `${m.warunkowe}%`,
            `${m.niezgodne}%`
          ]);
          
          (pdf as any).autoTable({
            head: [['Miesiąc', 'Zgodne', 'Warunkowe', 'Niezgodne']],
            body: trendTableData,
            startY: 160,
            theme: 'grid',
            headStyles: { fillColor: [251, 146, 60] }
          });
          
          // Stopka
          pdf.setFontSize(10);
          pdf.text(`Wygenerowano: ${new Date().toLocaleString('pl-PL')}`, 20, 280);
          pdf.text('PlexiSystem - System Zarządzania Produkcją', 105, 285, { align: 'center' });
          
          pdf.save(`Analiza_trendow_${dateRange.from}_${dateRange.to}.pdf`);
          break;
        }
        
        default:
          console.log('Nieznany typ raportu:', type);
      }
      
      return { data: reportData, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Błąd generowania raportu');
      setError(error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, [fetchReportData]);

  // Inicjalizuj przykładowe dane
  const initializeData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Zwróć przykładowe dane bez zapisywania do bazy
      console.log('Generowanie przykładowych danych kontroli jakości');
      return { data: 'mock', error: null };

      // Produkty i ich typowe defekty
      const products = [
        'Kaseton LED podświetlany',
        'Litery przestrzenne LED',
        'Plexi mleczne 3mm',
        'Dibond 3mm',
        'Baner frontlit 440g',
        'Naklejka monomeryczna',
        'Tablica informacyjna',
        'Roll-up standard',
        'Szyld reklamowy',
        'Grawerton laminat'
      ];

      const clients = [
        'ABC Marketing Sp. z o.o.',
        'SuperShop S.A.',
        'Restauracja Bella Vista',
        'Kancelaria Prawna Nowak',
        'Apteka Zdrowie',
        'Salon Fryzjerski Anna',
        'Auto Serwis Kowalski',
        'Hotel Polonia',
        'Pizzeria Roma',
        'Fitness Club Energy'
      ];

      const inspectors = [
        'Jan Kowalski',
        'Anna Nowak',
        'Piotr Wiśniewski',
        'Maria Zielińska'
      ];

      const defectTypes = [
        { type: 'dimensions', name: 'Wymiary poza tolerancją', severity: 'high' },
        { type: 'surface', name: 'Defekty powierzchni', severity: 'medium' },
        { type: 'assembly', name: 'Nieprawidłowy montaż', severity: 'high' },
        { type: 'electrical', name: 'Defekty elektryczne', severity: 'high' },
        { type: 'color', name: 'Niezgodność koloru', severity: 'medium' },
        { type: 'other', name: 'Inne', severity: 'low' }
      ];

      // Generuj dane dla ostatnich 3 miesięcy
      const today = new Date();
      const controls = [];
      
      for (let i = 90; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        
        // 2-5 kontroli dziennie (w dni robocze)
        if (date.getDay() !== 0 && date.getDay() !== 6) {
          const dailyControls = Math.floor(Math.random() * 4) + 2;
          
          for (let j = 0; j < dailyControls; j++) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const dayNum = String(controls.filter(c => 
              c.date.startsWith(`${year}-${month}`)
            ).length + 1).padStart(3, '0');
            
            // Losowe prawdopodobieństwo statusu
            const rand = Math.random();
            let status: 'approved' | 'conditional' | 'rejected';
            let defects = [];
            let responseTime = 0;
            let cost = 0;
            
            if (rand < 0.75) {
              status = 'approved';
              responseTime = Math.random() * 2 + 0.5; // 0.5-2.5h
            } else if (rand < 0.90) {
              status = 'conditional';
              // Dodaj 1-2 drobne defekty
              const numDefects = Math.floor(Math.random() * 2) + 1;
              for (let d = 0; d < numDefects; d++) {
                const defect = defectTypes[Math.floor(Math.random() * defectTypes.length)];
                defects.push({
                  type: defect.type,
                  description: `${defect.name} - wymaga poprawy`,
                  severity: 'low'
                });
              }
              responseTime = Math.random() * 4 + 1; // 1-5h
              cost = Math.random() * 500 + 100; // 100-600 zł
            } else {
              status = 'rejected';
              // Dodaj 2-4 poważne defekty
              const numDefects = Math.floor(Math.random() * 3) + 2;
              for (let d = 0; d < numDefects; d++) {
                const defect = defectTypes[Math.floor(Math.random() * defectTypes.length)];
                defects.push({
                  type: defect.type,
                  description: `${defect.name} - niedopuszczalne`,
                  severity: defect.severity as 'low' | 'medium' | 'high'
                });
              }
              responseTime = Math.random() * 8 + 2; // 2-10h
              cost = Math.random() * 2000 + 500; // 500-2500 zł
            }
            
            const control = {
              control_number: `KJ/${year}/${month}/${dayNum}`,
              date: date.toISOString().split('T')[0],
              client_name: clients[Math.floor(Math.random() * clients.length)],
              product: products[Math.floor(Math.random() * products.length)],
              status,
              inspector: inspectors[Math.floor(Math.random() * inspectors.length)],
              notes: status === 'approved' 
                ? 'Produkt zgodny ze specyfikacją' 
                : status === 'conditional'
                ? 'Produkt wymaga drobnych poprawek'
                : 'Produkt niezgodny ze specyfikacją, wymaga ponownej produkcji',
              parameters: {
                thickness: Math.random() * 5 + 1,
                dimensions: {
                  width: Math.floor(Math.random() * 2000) + 500,
                  height: Math.floor(Math.random() * 1000) + 300
                },
                defects: defects.length > 0 ? defects : undefined,
                response_time: Math.round(responseTime * 10) / 10,
                non_compliance_cost: Math.round(cost)
              },
              created_by: 'system',
              created_at: date.toISOString(),
              updated_at: date.toISOString()
            };
            
            controls.push(control);
          }
        }
      }

      // Nie wstawiamy do bazy - tylko zwracamy liczbę wygenerowanych danych
      console.log(`Wygenerowano ${90} przykładowych rekordów kontroli jakości`);
      return { data: 90, error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Błąd inicjalizacji danych');
      setError(error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  return {
    loading,
    error,
    fetchReportData,
    generateReport,
    initializeData
  };
};

// Funkcja do generowania przykładowych danych
function generateMockQualityData(dateRange: { from: string; to: string }) {
  const products = [
    'Kaseton LED podświetlany',
    'Litery przestrzenne LED',
    'Plexi mleczne 3mm',
    'Dibond 3mm',
    'Baner frontlit 440g',
    'Naklejka monomeryczna',
    'Tablica informacyjna',
    'Roll-up standard',
    'Szyld reklamowy',
    'Grawerton laminat'
  ];

  const clients = [
    'ABC Marketing Sp. z o.o.',
    'SuperShop S.A.',
    'Restauracja Bella Vista',
    'Kancelaria Prawna Nowak',
    'Apteka Zdrowie'
  ];

  const inspectors = [
    'Jan Kowalski',
    'Anna Nowak',
    'Piotr Wiśniewski',
    'Maria Zielińska'
  ];

  const defectTypes = [
    { type: 'dimensions', name: 'Wymiary poza tolerancją', severity: 'high' },
    { type: 'surface', name: 'Defekty powierzchni', severity: 'medium' },
    { type: 'assembly', name: 'Nieprawidłowy montaż', severity: 'high' },
    { type: 'electrical', name: 'Defekty elektryczne', severity: 'high' },
    { type: 'color', name: 'Niezgodność koloru', severity: 'medium' },
    { type: 'other', name: 'Inne', severity: 'low' }
  ];

  const startDate = new Date(dateRange.from);
  const endDate = new Date(dateRange.to);
  const controls = [];
  
  // Generuj kontrole dla każdego dnia w zakresie
  for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
    // 2-5 kontroli dziennie (w dni robocze)
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      const dailyControls = Math.floor(Math.random() * 4) + 2;
      
      for (let j = 0; j < dailyControls; j++) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const dayNum = String(controls.filter(c => 
          c.date.startsWith(`${year}-${month}`)
        ).length + 1).padStart(3, '0');
        
        // Losowe prawdopodobieństwo statusu
        const rand = Math.random();
        let status: 'approved' | 'conditional' | 'rejected';
        let defects = [];
        let responseTime = 0;
        let cost = 0;
        
        if (rand < 0.75) {
          status = 'approved';
          responseTime = Math.random() * 2 + 0.5; // 0.5-2.5h
        } else if (rand < 0.90) {
          status = 'conditional';
          // Dodaj 1-2 drobne defekty
          const numDefects = Math.floor(Math.random() * 2) + 1;
          for (let d = 0; d < numDefects; d++) {
            const defect = defectTypes[Math.floor(Math.random() * defectTypes.length)];
            defects.push({
              type: defect.type,
              description: `${defect.name} - wymaga poprawy`,
              severity: 'low'
            });
          }
          responseTime = Math.random() * 4 + 1; // 1-5h
          cost = Math.random() * 500 + 100; // 100-600 zł
        } else {
          status = 'rejected';
          // Dodaj 2-4 poważne defekty
          const numDefects = Math.floor(Math.random() * 3) + 2;
          for (let d = 0; d < numDefects; d++) {
            const defect = defectTypes[Math.floor(Math.random() * defectTypes.length)];
            defects.push({
              type: defect.type,
              description: `${defect.name} - niedopuszczalne`,
              severity: defect.severity as 'low' | 'medium' | 'high'
            });
          }
          responseTime = Math.random() * 8 + 2; // 2-10h
          cost = Math.random() * 2000 + 500; // 500-2500 zł
        }
        
        const control = {
          control_number: `KJ/${year}/${month}/${dayNum}`,
          date: date.toISOString().split('T')[0],
          client_name: clients[Math.floor(Math.random() * clients.length)],
          product: products[Math.floor(Math.random() * products.length)],
          status,
          inspector: inspectors[Math.floor(Math.random() * inspectors.length)],
          notes: status === 'approved' 
            ? 'Produkt zgodny ze specyfikacją' 
            : status === 'conditional'
            ? 'Produkt wymaga drobnych poprawek'
            : 'Produkt niezgodny ze specyfikacją, wymaga ponownej produkcji',
          parameters: {
            thickness: Math.random() * 5 + 1,
            dimensions: {
              width: Math.floor(Math.random() * 2000) + 500,
              height: Math.floor(Math.random() * 1000) + 300
            },
            defects: defects.length > 0 ? defects : undefined,
            response_time: Math.round(responseTime * 10) / 10,
            non_compliance_cost: Math.round(cost)
          },
          created_by: 'system',
          created_at: date.toISOString(),
          updated_at: date.toISOString()
        };
        
        controls.push(control);
      }
    }
  }
  
  return controls;
}

// Funkcja pomocnicza do przetwarzania danych
function processReportData(controls: any[], dateRange: { from: string; to: string }): QualityReportData {
  // Grupuj kontrole po miesiącach
  const monthlyData = new Map<string, { approved: number; conditional: number; rejected: number; total: number }>();
  
  // Zlicz typy defektów
  const defectCounts = new Map<string, number>();
  
  // Grupuj po produktach
  const productData = new Map<string, { checks: number; passed: number }>();
  
  // Statystyki ogólne
  let totalChecks = 0;
  let totalApproved = 0;
  let totalResponseTime = 0;
  let totalCost = 0;

  controls.forEach(control => {
    const date = new Date(control.date);
    const monthKey = date.toLocaleDateString('pl-PL', { month: 'short', year: 'numeric' });
    
    // Aktualizuj dane miesięczne
    if (!monthlyData.has(monthKey)) {
      monthlyData.set(monthKey, { approved: 0, conditional: 0, rejected: 0, total: 0 });
    }
    
    const monthData = monthlyData.get(monthKey)!;
    monthData.total++;
    
    switch (control.status) {
      case 'approved':
        monthData.approved++;
        totalApproved++;
        break;
      case 'conditional':
        monthData.conditional++;
        totalApproved++; // Warunkowe też są uważane za zaakceptowane
        break;
      case 'rejected':
        monthData.rejected++;
        break;
    }
    
    // Zlicz defekty (jeśli są w parametrach)
    if (control.parameters?.defects && Array.isArray(control.parameters.defects)) {
      control.parameters.defects.forEach((defect: any) => {
        const defectType = typeof defect === 'string' ? defect : defect.type;
        const currentCount = defectCounts.get(defectType) || 0;
        defectCounts.set(defectType, currentCount + 1);
      });
    }
    
    // Aktualizuj dane produktów
    const productKey = control.product;
    if (!productData.has(productKey)) {
      productData.set(productKey, { checks: 0, passed: 0 });
    }
    
    const product = productData.get(productKey)!;
    product.checks++;
    if (control.status === 'approved' || control.status === 'conditional') {
      product.passed++;
    }
    
    // Statystyki
    totalChecks++;
    if (control.parameters?.response_time) {
      totalResponseTime += control.parameters.response_time;
    }
    if (control.parameters?.non_compliance_cost) {
      totalCost += control.parameters.non_compliance_cost;
    }
  });

  // Konwertuj mapy na tablice dla wykresów
  const complianceData = Array.from(monthlyData.entries()).map(([month, data]) => ({
    month,
    zgodne: Math.round((data.approved / data.total) * 100),
    warunkowe: Math.round((data.conditional / data.total) * 100),
    niezgodne: Math.round((data.rejected / data.total) * 100)
  }));

  // Przygotuj dane o defektach
  const defectTypeNames = {
    'dimensions': 'Wymiary poza tolerancją',
    'surface': 'Defekty powierzchni',
    'assembly': 'Nieprawidłowy montaż',
    'electrical': 'Defekty elektryczne',
    'color': 'Niezgodność koloru',
    'other': 'Inne'
  };
  
  const defectTypes = Array.from(defectCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, value], index) => ({
      name: defectTypeNames[name as keyof typeof defectTypeNames] || name,
      value,
      color: ['#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#6B7280'][index]
    }));

  // Przygotuj dane o produktach
  const productQuality = Array.from(productData.entries())
    .map(([product, data]) => ({
      product,
      checks: data.checks,
      passed: data.passed,
      passRate: Math.round((data.passed / data.checks) * 100 * 10) / 10
    }))
    .sort((a, b) => b.checks - a.checks)
    .slice(0, 5);

  // Oblicz statystyki
  const statistics = {
    totalChecks,
    avgComplianceRate: totalChecks > 0 ? Math.round((totalApproved / totalChecks) * 100 * 10) / 10 : 0,
    avgResponseTime: totalChecks > 0 ? Math.round(totalResponseTime / totalChecks * 10) / 10 : 0,
    nonComplianceCost: totalCost
  };

  return {
    complianceData,
    defectTypes,
    productQuality,
    statistics
  };
}
